import React from 'react';
import { PropertyPathStackProvider, useCurrentPath } from '../hooks/usePropertyPath';
import { ProductsListRecord } from 'types/dynamic-forms/records/ProductsListRecord';
import DynamicContentRenderer from '../DynamicContentRenderer';
import useProperty from '../hooks/useProperty';
import { AddCustomBlockButton } from 'components/v2/buttons/AddCustomBlockButton';
import { FieldPanelFullWidth, HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { DeleteProductIconButton } from 'components/v2/compiler/ProductInformationTab';
import FontIcon from 'components/icons/FontIcon';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import useNestedErrors from '../hooks/useNestedErrors';

const ProductsListRenderer: React.FC<ProductsListRecord> = ({ propertyId, children }) => {
  const products = useProperty<Record<string, any>[]>([propertyId]);
  const singleOrMultipleProducts = useProperty(['singleOrMultipleProducts']);
  if (!products.isVisible) {
    return null;
  }

  const isDisabled = products.isDisabled;
  const canAddMany = singleOrMultipleProducts.value && singleOrMultipleProducts.value !== 'SingleProduct';

  return (
    <>
      {products.value.map((product, i, arr) => (
        <PropertyPathStackProvider key={i} pushPath={[propertyId, i]}>
          {arr.length >= 2 && (
            <FieldPanelFullWidth style={{ marginTop: i >= 1 ? 80 : 16 }}>
              <div className="flex justify-content-between">
                <HeaderCaptionSemiBold>
                  {product.productName || 'Unnamed product'}
                  <ProductError />
                </HeaderCaptionSemiBold>

                {!isDisabled && (
                  <DeleteProductIconButton
                    icon={<FontIcon style={{ fontSize: '0.75rem' }} icon="delete" />}
                    text
                    onClick={() => products.setValue(arr.filter((v) => v !== product))}
                  />
                )}
              </div>
            </FieldPanelFullWidth>
          )}

          <DynamicContentRenderer schema={children} />
        </PropertyPathStackProvider>
      ))}

      {canAddMany && !isDisabled && (
        <AddCustomBlockButton caption="Add product" onClick={() => products.setValue([...products.value, {}])} />
      )}
    </>
  );
};

const ProductError = () => {
  const path = useCurrentPath();
  const errors = useNestedErrors(path);

  if (errors.length === 0) {
    return null;
  }

  return <TooltipErrorIcon content={errors} />;
};

export default ProductsListRenderer;
